import React from "react";
import ReactDOM from "react-dom";

export interface AnimationGenerationModalState {
  phase: 'generating' | 'success' | 'error';
  approachName: string;
  problemTitle?: string;
  message?: string;
  timeElapsed?: number;
  animationUrl?: string;
}

interface AnimationGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: AnimationGenerationModalState;
  onOpenAnimation?: (url: string) => void;
}

export const AnimationGenerationModal: React.FC<AnimationGenerationModalProps> = ({
  isOpen,
  onClose,
  state,
  onOpenAnimation
}) => {
  // Debug logging
  React.useEffect(() => {
    console.log('🎬 Animation Modal state changed:', { isOpen, phase: state.phase, approachName: state.approachName });
  }, [isOpen, state.phase, state.approachName]);

  const getModalContent = () => {
    switch (state.phase) {
      case 'generating':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-purple-600">
              <svg className="w-8 h-8 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          ),
          title: "Generating Animation",
          message: `Creating interactive visualization for "${state.approachName}"`,
          subtitle: `AI is crafting step-by-step animation... (50-120 seconds)`,
          bgColor: "bg-white",
          borderColor: "border-purple-300", 
          textColor: "text-gray-700",
          iconColor: "text-purple-600"
        };

      case 'success':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-green-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1M9 16v-6a4 4 0 118 0v6M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          ),
          title: "Animation Ready!",
          message: `Interactive visualization for "${state.approachName}" is complete`,
          subtitle: "Click below to open in new tab",
          bgColor: "bg-white",
          borderColor: "border-green-300",
          textColor: "text-gray-700",
          iconColor: "text-green-600"
        };

      case 'error':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-red-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          ),
          title: "Animation Failed",
          message: state.message || "Failed to generate animation",
          subtitle: "Please try again",
          bgColor: "bg-white",
          borderColor: "border-red-300",
          textColor: "text-gray-700",
          iconColor: "text-red-600"
        };

      default:
        return null;
    }
  };

  const showCloseButton = state.phase === 'success' || state.phase === 'error';
  const showOpenButton = state.phase === 'success' && state.animationUrl;

  const handleOpenAnimation = () => {
    if (state.animationUrl) {
      if (onOpenAnimation) {
        onOpenAnimation(state.animationUrl);
      } else {
        window.open(state.animationUrl, '_blank', 'noopener,noreferrer');
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  const content = getModalContent();
  if (!content) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-10 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-2xl border-4 ${content.borderColor} p-6 max-w-md w-full mx-4`} style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'}}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            {content.icon}
            <h3 className={`text-base font-semibold text-center ${content.textColor}`}>
              {content.title}
            </h3>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="text-center">
          <p className={`text-sm ${content.textColor} mb-2 leading-relaxed`}>
            {content.message}
          </p>
          <p className={`text-xs text-gray-500`}>
            {content.subtitle}
          </p>

          {/* Problem title */}
          {state.problemTitle && (
            <p className="text-xs text-gray-600 mt-2 italic">
              Problem: {state.problemTitle}
            </p>
          )}

          {/* Time elapsed for generation */}
          {state.phase === 'generating' && state.timeElapsed && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Time elapsed: {Math.floor(state.timeElapsed / 1000)}s
              </p>
              {state.timeElapsed > 20000 && (
                <p className="text-xs text-gray-500 mt-1">
                  Creating interactive controls and visualizations...
                </p>
              )}
              {state.timeElapsed > 40000 && (
                <p className="text-xs text-gray-500 mt-1">
                  Almost ready! Adding final touches...
                </p>
              )}
            </div>
          )}

          {/* Progress bar for generation */}
          {state.phase === 'generating' && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-purple-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ 
                    width: state.timeElapsed 
                      ? `${Math.min(90, (state.timeElapsed / 45000) * 100)}%` 
                      : '15%' 
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-center gap-3">
          {showOpenButton && (
            <button
              onClick={handleOpenAnimation}
              className="px-6 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors font-medium"
            >
              🎬 Open Animation
            </button>
          )}
          
          {state.phase === 'error' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          )}

          {state.phase === 'success' && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};